// One source for every public page's search metadata. The Vite plugin writes the home page's head from it,
// the prerender step writes each page's head, body, sitemap and llms.txt from it, and the client keeps the
// head in step on navigation, so a page cannot ship half a contract.
import { RULES } from '../shared/rules.ts';

export const SITE = 'https://sloplens.tryraisins.dev';
export const SITE_NAME = 'SlopLens';
const AUTHOR = { name: 'Seun Sowemimo', url: 'https://tryraisins.dev/' };
const OG_IMAGE = `${SITE}/og-image.png`;
const OG_IMAGE_ALT = 'SlopLens: How much slop is on that website? A score card reads 82 out of 100, Certified slop.';

const count = (category: 'slop' | 'quality', supporting = false) => RULES.filter(r => r.category === category && !!r.supporting === supporting).length;
export const RULE_COUNTS = { total: RULES.length, slop: count('slop'), supporting: count('slop', true), quality: count('quality') };

export type PageId = 'home' | 'how' | 'missing';
export type PageMeta = {
  path: string; title: string; description: string; socialTitle: string; socialDescription: string;
  // Set when the content materially changes, not on every build; it becomes the sitemap's lastmod.
  updated?: string; index: boolean;
};

export const PAGES: Record<PageId, PageMeta> = {
  home: {
    path: '/',
    title: 'SlopLens: free AI slop checker for any website',
    description: 'Paste a link to get a free slop score out of 100. SlopLens checks the page on desktop and phone for template habits such as stock phrases, gradient headlines and identical feature cards, then notes what to change. No sign-up needed.',
    socialTitle: 'How much slop is on that website?',
    socialDescription: 'Paste a link and SlopLens checks the page on desktop and phone for buzzword copy, gradient headlines and cookie-cutter cards, then gives it a slop score out of 100.',
    updated: '2026-10-26', index: true,
  },
  how: {
    path: '/how-it-works',
    title: `How the slop score works: all ${RULE_COUNTS.total} website checks | SlopLens`,
    description: `SlopLens scores a page with ${RULE_COUNTS.total} published rules: ${RULE_COUNTS.slop} template habits such as buzzword copy and gradient headlines, ${RULE_COUNTS.supporting} supporting signals and ${RULE_COUNTS.quality} usability problems. See what each rule looks for, its point limit and what the score cannot tell you.`,
    socialTitle: 'How the SlopLens slop score adds up',
    socialDescription: `Every rule SlopLens uses to score a website, from buzzword copy to unlabeled buttons, with the point limits and the limits of the score itself.`,
    updated: '2026-10-26', index: true,
  },
  missing: {
    path: '/404',
    title: 'Page not found | SlopLens',
    description: 'This address does not match a SlopLens page. Check a website for slop from the home page instead.',
    socialTitle: 'Page not found', socialDescription: 'This address does not match a SlopLens page.',
    index: false,
  },
};

export const canonical = (page: PageId) => SITE + PAGES[page].path;

const person = { '@type': 'Person', '@id': `${AUTHOR.url}#person`, name: AUTHOR.name, url: AUTHOR.url };
const website = { '@type': 'WebSite', '@id': `${SITE}/#website`, url: `${SITE}/`, name: SITE_NAME, inLanguage: 'en', publisher: { '@id': person['@id'] } };
const application = {
  '@type': 'WebApplication', '@id': `${SITE}/#app`, name: SITE_NAME, url: `${SITE}/`,
  description: 'SlopLens opens a web page at desktop and phone sizes and scores it out of 100 for how template-made it looks, listing the patterns it found, such as buzzword copy, gradient headlines and cookie-cutter feature cards.',
  applicationCategory: 'DeveloperApplication', operatingSystem: 'Any', browserRequirements: 'Requires JavaScript.',
  isAccessibleForFree: true, offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }, image: OG_IMAGE,
  featureList: ['Slop score out of 100', 'Desktop and phone screenshots', 'Numbered notes with evidence and a suggested fix', `${RULE_COUNTS.total} published rules`],
  creator: { '@id': person['@id'] },
};

export function structuredData(page: PageId): object | null {
  if (page === 'home') return { '@context': 'https://schema.org', '@graph': [website, application, person] };
  if (page === 'how') return {
    '@context': 'https://schema.org',
    '@graph': [{ '@type': 'WebPage', '@id': `${canonical('how')}#page`, url: canonical('how'), name: PAGES.how.socialTitle, description: PAGES.how.description, inLanguage: 'en', isPartOf: { '@id': website['@id'] }, about: { '@id': application['@id'] }, dateModified: PAGES.how.updated }, website, application, person],
  };
  return null;
}

const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Every per-page head tag, as HTML. Shared tags such as icons and theme color stay in index.html. */
export function headTags(page: PageId): string {
  const meta = PAGES[page];
  const url = canonical(page);
  const tags = [
    `<title>${escape(meta.title)}</title>`,
    `<meta name="description" content="${escape(meta.description)}" />`,
    meta.index ? `<link rel="canonical" href="${url}" />` : '',
    `<meta name="robots" content="${meta.index ? 'index, follow, max-image-preview:large' : 'noindex, follow'}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    meta.index ? `<meta property="og:url" content="${url}" />` : '',
    `<meta property="og:title" content="${escape(meta.socialTitle)}" />`,
    `<meta property="og:description" content="${escape(meta.socialDescription)}" />`,
    `<meta property="og:image" content="${OG_IMAGE}" />`,
    `<meta property="og:image:type" content="image/png" />`,
    `<meta property="og:image:width" content="1200" />`,
    `<meta property="og:image:height" content="630" />`,
    `<meta property="og:image:alt" content="${escape(OG_IMAGE_ALT)}" />`,
    `<meta property="og:locale" content="en_US" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escape(meta.socialTitle)}" />`,
    `<meta name="twitter:description" content="${escape(meta.socialDescription)}" />`,
    `<meta name="twitter:image" content="${OG_IMAGE}" />`,
    `<meta name="twitter:image:alt" content="${escape(OG_IMAGE_ALT)}" />`,
  ];
  const data = structuredData(page);
  // Escaping "<" keeps rule text from ever closing the script element.
  if (data) tags.push(`<script type="application/ld+json">${JSON.stringify(data).replace(/</g, '\\u003c')}</script>`);
  return tags.filter(Boolean).join('\n    ');
}

/** Keeps the live document's head in step with the page after client-side navigation. */
export function applyHead(page: PageId, title = PAGES[page].title) {
  const meta = PAGES[page];
  document.title = title;
  const set = (selector: string, attribute: string, value: string | null) => {
    const element = document.head.querySelector(selector);
    if (element && value !== null) element.setAttribute(attribute, value);
  };
  set('meta[name="description"]', 'content', meta.description);
  set('link[rel="canonical"]', 'href', meta.index ? canonical(page) : null);
  set('meta[name="robots"]', 'content', meta.index ? 'index, follow, max-image-preview:large' : 'noindex, follow');
  set('meta[property="og:url"]', 'content', meta.index ? canonical(page) : null);
  set('meta[property="og:title"]', 'content', meta.socialTitle);
  set('meta[property="og:description"]', 'content', meta.socialDescription);
  set('meta[name="twitter:title"]', 'content', meta.socialTitle);
  set('meta[name="twitter:description"]', 'content', meta.socialDescription);
}

export function sitemap(): string {
  const urls = (Object.keys(PAGES) as PageId[]).filter(id => PAGES[id].index).map(id => `  <url>\n    <loc>${canonical(id)}</loc>\n    <lastmod>${PAGES[id].updated}</lastmod>\n  </url>`);
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls.join('\n')}\n</urlset>\n`;
}

/** A plain-text index for agents that choose to read /llms.txt. It is not a ranking signal. */
export function llmsText(): string {
  const group = (category: 'slop' | 'quality', supporting = false) => RULES.filter(r => r.category === category && !!r.supporting === supporting).map(r => `- ${r.name}: ${r.plain}`).join('\n');
  return `# ${SITE_NAME}

> ${SITE_NAME} is a free web tool that opens a public web page at desktop and phone sizes and gives it a slop score out of 100 for how template-made it looks. Each finding comes with a numbered note, the evidence on a screenshot and a suggested fix. There is no sign-up.

The score describes visible patterns on a page. It cannot tell who or what built the website, and a high score is not proof that anyone used AI. ${SITE_NAME} only reads the address it is given, never signs in or fills in forms, checks English copy only, and reports pages behind a bot check as unscored. Reports and screenshots are deleted an hour after a scan.

${SITE_NAME} is made by ${AUTHOR.name} (${AUTHOR.url}).

## Pages

- [Check a website](${canonical('home')}): paste a link and get a slop score with numbered notes.
- [How the slop score works](${canonical('how')}): every rule, its point limit, how related rules share a limit, and what the score cannot tell you.

## Slop signals (${RULE_COUNTS.slop})

These template habits carry most of the score.

${group('slop')}

## Supporting signals (${RULE_COUNTS.supporting})

These add points only when at least one slop signal was also found, and never more than that evidence.

${group('slop', true)}

## Quality issues (${RULE_COUNTS.quality})

These are usability problems rather than style choices, so they add only a few points.

${group('quality')}
`;
}
